import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Users, Edit3, UserPlus, Building } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

const TeamCard = ({ team, onEdit, onManageMembers }) => {
  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  const getInitials = (name) => {
    if (!name) return '??';
    const names = name.split(' ');
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return names[0][0].toUpperCase() + names[names.length - 1][0].toUpperCase();
  };

  return (
    <motion.div variants={cardVariants}>
      <Card className="bg-slate-800/70 border-slate-700/50 shadow-xl hover:shadow-purple-500/20 transition-shadow duration-300 flex flex-col h-full">
        <CardHeader className="pb-3">
          <div className="flex justify-between items-start">
            <CardTitle className="text-xl text-slate-100">{team.name}</CardTitle>
            <Users size={24} className="text-purple-400" />
          </div>
          {team.department && (
            <CardDescription className="text-slate-400 pt-1 flex items-center">
              <Building size={14} className="mr-2 text-purple-300" />
              {team.department}
            </CardDescription>
          )}
        </CardHeader>
        <CardContent className="space-y-3 text-sm flex-grow">
          <h4 className="text-slate-300 font-semibold mb-2">Members ({team.employees?.length || 0}):</h4>
          {team.employees && team.employees.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {team.employees.slice(0, 5).map((employee) => (
                <div key={employee.id} className="flex items-center bg-slate-700/50 p-2 rounded-md" title={employee.name}>
                  <Avatar className="h-6 w-6 mr-2">
                    <AvatarImage src={employee.photo_url} alt={employee.name} />
                    <AvatarFallback className="text-xs bg-slate-600 text-slate-200">
                      {getInitials(employee.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-xs text-slate-200 truncate max-w-[80px]">{employee.name}</span>
                </div>
              ))}
              {team.employees.length > 5 && (
                <div className="flex items-center bg-slate-700/50 p-2 rounded-md">
                  <span className="text-xs text-slate-300">+{team.employees.length - 5} more</span>
                </div>
              )}
            </div>
          ) : (
            <p className="text-slate-500 italic">No members assigned yet.</p>
          )}
        </CardContent>
        <CardFooter className="pt-4 flex justify-end space-x-2">
          <Button variant="outline" size="sm" onClick={() => onEdit(team)} className="text-purple-400 border-purple-400/50 hover:bg-purple-400/10 hover:text-purple-300">
            <Edit3 size={16} className="mr-1" /> Edit Team
          </Button>
          <Button variant="default" size="sm" onClick={() => onManageMembers(team)} className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white">
            <UserPlus size={16} className="mr-1" /> Manage Members
          </Button>
        </CardFooter>
      </Card>
    </motion.div>
  );
};

export default TeamCard;